import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, MessageCircleIcon } from 'lucide-react';
import { Button } from '../Button';
import { Halo, RuleDot } from '../Decor';
import { useLeadForm } from './useLeadForm';
import { NEED_OPTIONS } from '../../types/lead';
import { WHATSAPP_URL } from '../../data/site';
import { SECTION_IDS } from '../../utils/scroll';
const floridaOptions = ['Sí', 'No', 'No estoy segura'];
const etapaOptions = ['Tengo una idea', 'Estoy lista para crear mi empresa', 'Ya tengo una empresa'];
const fieldBase = 'w-full rounded-2xl border bg-lavender-50/60 px-4 py-3.5 text-[0.975rem] text-ink placeholder:text-muted/60 transition-[background-color,border-color,box-shadow] duration-200 ease-premium focus:border-plum-600 focus:bg-ivory focus:shadow-soft focus:outline-none';
const stepTransition = {
  duration: 0.24,
  ease: [0.23, 1, 0.32, 1] as const
};
function FieldError({
  id,
  message
}: {
  id: string;
  message?: string;
}) {
  if (!message) return null;
  return <p id={id} role="alert" className="mt-2 text-xs text-plum-700" data-id="element-127" data-mp="3bu8yupoglvdz">
      {message}
    </p>;
}
function ChoiceChip({
  label,
  selected,
  onClick
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} className={`inline-flex items-center gap-2.5 rounded-pill border px-4 py-3 text-left text-[0.9rem] transition-[background-color,border-color,color,box-shadow] duration-200 ease-premium sm:text-sm ${selected ? 'border-plum-700 bg-lavender-100 text-plum-900 shadow-hair' : 'border-lavender-200 bg-ivory text-ink hover:border-lavender-300 hover:bg-lavender-50/60'}`} data-id="element-128" data-mp="2wxrnvzk6miry">
      <span aria-hidden="true" className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 ease-premium ${selected ? 'border-plum-700 bg-plum-700 text-ivory' : 'border-lavender-300 text-transparent'}`} data-id="element-129" data-mp="2985obazwe1jf">
        <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} data-id="element-130" data-mp="1zkbb4l2tnv1w" />
      </span>
      {label}
    </button>;
}
export function LeadForm() {
  const form = useLeadForm();
  return <section id={SECTION_IDS.formulario} aria-labelledby="formulario-titulo" className="relative scroll-mt-24 overflow-hidden bg-gradient-to-b from-ivory to-lavender-50 sm:scroll-mt-28" data-id="element-131" data-mp="1ln4t4spxa0dq">
      <Halo soft className="-left-32 bottom-0 h-[28rem] w-[28rem]" data-id="element-132" data-mp="2ib1c248u17s4" />

      <div className="relative mx-auto max-w-content px-5 py-12 sm:px-8 sm:py-16 lg:py-24" data-id="element-133" data-mp="2ia9ewozl71zq">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16" data-id="element-134" data-mp="24j4li4mtb5xu">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-135" data-mp="29h68h0nczpel">
            <RuleDot data-id="element-136" data-mp="2x7s5zxzqlrac" />
            <h2 id="formulario-titulo" className="mt-5 font-display text-[1.7rem] leading-[1.16] text-plum-900 sm:text-4xl lg:text-[2.5rem]" data-id="element-137" data-mp="2x7jpzx8bi5ux">
              Cuéntanos qué quieres construir.
            </h2>
            <p className="mt-3.5 max-w-md text-[1rem] leading-relaxed text-muted sm:mt-5 sm:text-[1.0625rem]" data-id="element-138" data-mp="37c8hkf66om8c">
              Responde unas preguntas breves para conocer tu proyecto y ayudarte a
              identificar el mejor próximo paso.
            </p>
            <p className="mt-5 text-sm text-plum-500 sm:mt-8" data-id="element-139" data-mp="3g53pxhqy6y2c">
              Servicios enfocados en negocios en Florida.
            </p>
          </div>

          {form.submitted ? <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={stepTransition} role="status" aria-live="polite" className="rounded-xl2 border border-lavender-200 bg-ivory p-6 text-ink shadow-lift sm:p-10" data-id="element-140" data-mp="2oefpk7x3lwpq">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-pill bg-lavender-100 text-plum-700" data-id="element-141" data-mp="hp13j3h9lzku">
                <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-142" data-mp="2icdsv7nwyxk2" />
              </span>
              <h3 className="mt-5 font-display text-2xl leading-snug text-plum-900 sm:mt-6 sm:text-3xl" data-id="element-143" data-mp="2nb78b5mrgymv">
                Gracias. Recibimos la información de tu proyecto.
              </h3>
              <p className="mt-3.5 max-w-md text-[1rem] leading-relaxed text-muted" data-id="element-144" data-mp="2sco4inuavwx4">
                Revisaremos tus respuestas para conocer mejor lo que necesitas.
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center" data-id="element-145" data-mp="318vs0a53dzqc">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-analytics-id="form_success_whatsapp" className="inline-flex items-center justify-center gap-2 rounded-pill bg-plum-700 px-6 py-3.5 text-sm font-medium text-ivory transition-colors duration-200 ease-out hover:bg-plum-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum-700" data-id="element-146" data-mp="3688fmx3sfatz">
                  <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-147" data-mp="18e49deft9u5i" />
                  Continuar por WhatsApp
                </a>
                <button type="button" onClick={form.reset} className="text-sm text-muted underline underline-offset-4 transition-colors duration-200 ease-out hover:text-plum-800" data-id="element-148" data-mp="14br7x283kiej">
                  Enviar otra respuesta
                </button>
              </div>
            </motion.div> : <form noValidate onSubmit={form.handleSubmit} onFocusCapture={form.markStarted} data-analytics-id="form_start" data-analytics-step={form.step} data-analytics-started={form.started ? 'true' : 'false'} className="relative rounded-xl2 border border-lavender-200 bg-ivory p-5 text-ink shadow-lift sm:p-9" data-id="element-149" data-mp="1nb54zt14i999">
              <div className="flex items-center gap-4" data-id="element-150" data-mp="2ty9amuqwc280">
                <span className="eyebrow whitespace-nowrap text-plum-600" data-id="element-151" data-mp="2ywb66f5idu0d">
                  Paso {form.step} de 2
                </span>
                <span aria-hidden="true" className="h-[4px] flex-1 overflow-hidden rounded-pill bg-lavender-100" data-id="element-152" data-mp="2l5phg9tdipuk">
                  <motion.span className="block h-full rounded-pill bg-plum-700" initial={false} animate={{
                width: form.step === 1 ? '50%' : '100%'
              }} transition={stepTransition} data-id="element-153" data-mp="2b6qcp2bqbecu" />
                </span>
              </div>

              <AnimatePresence mode="wait" initial={false} data-id="element-154" data-mp="266viopwnq8nk">
                {form.step === 1 ? <motion.div key="paso-1" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={stepTransition} data-id="element-155" data-mp="efbyi9h859a">
                    <h3 className="mt-5 font-display text-xl leading-snug text-plum-900 sm:text-2xl" data-id="element-156" data-mp="1kwzmo2vo0ais">
                      Primero, cuéntanos sobre tu negocio
                    </h3>

                    <fieldset className="mt-6" data-id="element-157" data-mp="1txeb3b3xe5qi">
                      <legend className="mb-3 text-sm font-medium text-plum-900" data-id="element-158" data-mp="1twmebwyle1yn">
                        ¿Tu negocio estará registrado en Florida?
                      </legend>
                      <div className="flex flex-wrap gap-2.5" data-id="element-159" data-mp="23o0hcepr5jwj">
                        {floridaOptions.map(option => <ChoiceChip key={option} label={option} selected={form.values.florida === option} onClick={() => form.setField('florida', option)} data-id="element-160" data-mp="2hlp1fkbzwn0j" />)}
                      </div>
                      <FieldError id="error-florida" message={form.errors.florida} data-id="element-161" data-mp="3tjugdemk5pcl" />
                    </fieldset>

                    <fieldset className="mt-6" data-id="element-162" data-mp="1fa0iy2hg99c">
                      <legend className="mb-3 text-sm font-medium text-plum-900" data-id="element-163" data-mp="1dz2levpxvq5">
                        ¿En qué etapa estás?
                      </legend>
                      <div className="flex flex-wrap gap-2.5" data-id="element-164" data-mp="b5uzimm2jfss">
                        {etapaOptions.map(option => <ChoiceChip key={option} label={option} selected={form.values.etapa === option} onClick={() => form.setField('etapa', option)} data-id="element-165" data-mp="3am1lmzm38lqa" />)}
                      </div>
                      <FieldError id="error-etapa" message={form.errors.etapa} data-id="element-166" data-mp="yv630buszoam" />
                    </fieldset>

                    <fieldset className="mt-6" data-id="element-167" data-mp="13u0nrjcbzmxd">
                      <legend className="mb-3 text-sm font-medium text-plum-900" data-id="element-168" data-mp="13spjliez4d5q">
                        ¿Qué necesitas?{' '}
                        <span className="font-normal text-muted" data-id="element-169" data-mp="2oe5s8rd0f73o">(puedes elegir varias)</span>
                      </legend>
                      <div className="grid gap-2.5 sm:grid-cols-2" data-id="element-170" data-mp="2elzxj9vdibks">
                        {NEED_OPTIONS.map(option => <ChoiceChip key={option.id} label={option.label} selected={form.values.necesidades.includes(option.id)} onClick={() => form.toggleNeed(option.id)} data-id="element-171" data-mp="29mncmuo2beku" />)}
                      </div>
                    </fieldset>

                    <Button type="button" size="lg" analyticsId="form_step_1_continue" onClick={form.goNext} className="mt-7 w-full sm:w-auto" data-id="element-172" data-mp="1qpm3cxnh6sb3">
                      Continuar
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-premium group-hover:translate-x-0.5" aria-hidden="true" data-id="element-173" data-mp="172sxpre66zai" />
                    </Button>
                  </motion.div> : <motion.div key="paso-2" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={stepTransition} data-id="element-174" data-mp="1c3qcdtzz1b79">
                    <h3 className="mt-5 font-display text-xl leading-snug text-plum-900 sm:text-2xl" data-id="element-175" data-mp="y4h3zgi0bldu">
                      ¿Dónde podemos contactarte?
                    </h3>

                    <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-176" data-mp="5mlehbwly431">
                      <div className="sm:col-span-2" data-id="element-177" data-mp="5ks69s6cjbsx">
                        <label htmlFor="nombre" className="mb-2 block text-sm font-medium text-plum-900" data-id="element-178" data-mp="n8quoxwwsze">
                          Nombre
                        </label>
                        <input id="nombre" name="nombre" type="text" autoComplete="name" required value={form.values.nombre} onChange={e => form.setField('nombre', e.target.value)} aria-invalid={Boolean(form.errors.nombre)} aria-describedby={form.errors.nombre ? 'error-nombre' : undefined} className={`${fieldBase} ${form.errors.nombre ? 'border-plum-600' : 'border-lavender-200'}`} data-id="element-179" data-mp="22d7kzu923g78" />
                        <FieldError id="error-nombre" message={form.errors.nombre} data-id="element-180" data-mp="1envckwljzc5z" />
                      </div>

                      <div data-id="element-181" data-mp="3h98sw7ilhnl8">
                        <label htmlFor="email" className="mb-2 block text-sm font-medium text-plum-900" data-id="element-182" data-mp="3h9ii3svtfqpv">
                          Email
                        </label>
                        <input id="email" name="email" type="email" autoComplete="email" required value={form.values.email} onChange={e => form.setField('email', e.target.value)} aria-invalid={Boolean(form.errors.email)} aria-describedby={form.errors.email ? 'error-email' : undefined} className={`${fieldBase} ${form.errors.email ? 'border-plum-600' : 'border-lavender-200'}`} data-id="element-183" data-mp="32lxjoc9vyfoa" />
                        <FieldError id="error-email" message={form.errors.email} data-id="element-184" data-mp="qb4gdyv0a27h" />
                      </div>

                      <div data-id="element-185" data-mp="ucg41yubkdd1">
                        <label htmlFor="telefono" className="mb-2 block text-sm font-medium text-plum-900" data-id="element-186" data-mp="ubocr07r91sk">
                          Teléfono
                        </label>
                        <input id="telefono" name="telefono" type="tel" inputMode="tel" autoComplete="tel" required value={form.values.telefono} onChange={e => form.setField('telefono', e.target.value)} aria-invalid={Boolean(form.errors.telefono)} aria-describedby={form.errors.telefono ? 'error-telefono' : undefined} className={`${fieldBase} ${form.errors.telefono ? 'border-plum-600' : 'border-lavender-200'}`} data-id="element-187" data-mp="7ecz1uz0xx62" />
                        <FieldError id="error-telefono" message={form.errors.telefono} data-id="element-188" data-mp="3eqe4rlnakhk2" />
                      </div>
                    </div>

                    <div className="mt-5" data-id="element-189" data-mp="39pgm77yddli1">
                      <label htmlFor="mensaje" className="mb-2 block text-sm font-medium text-plum-900" data-id="element-190" data-mp="3onsl7l97t6cd">
                        Cuéntanos brevemente sobre tu negocio{' '}
                        <span className="font-normal text-muted" data-id="element-191" data-mp="1agfy427hhnv">(opcional)</span>
                      </label>
                      <textarea id="mensaje" name="mensaje" rows={4} value={form.values.mensaje} onChange={e => form.setField('mensaje', e.target.value)} className={`${fieldBase} resize-none border-lavender-200`} data-id="element-192" data-mp="3tfkbduogla1z" />
                    </div>

                    {form.status === 'error' && <p role="alert" className="mt-5 text-sm text-plum-800" data-id="element-193" data-mp="fygfbums8ig3">
                        No pudimos enviar tu información. Revisa tu conexión e inténtalo de
                        nuevo.
                      </p>}

                    <div className="mt-7 flex flex-col gap-3 sm:flex-row-reverse sm:items-center sm:justify-end sm:gap-4" data-id="element-194" data-mp="73a8qtfotwci">
                      <Button type="submit" size="lg" analyticsId="form_submit" disabled={form.status === 'sending'} data-id="element-195" data-mp="3tcl4whzl40eb">
                        {form.status === 'sending' ? 'Enviando…' : 'Quiero comenzar'}
                      </Button>
                      <button type="button" onClick={form.goBack} className="inline-flex items-center justify-center gap-2 py-2 text-sm text-muted transition-colors duration-200 ease-out hover:text-plum-800" data-id="element-196" data-mp="3ocyvykqsh6bb">
                        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-197" data-mp="1l57xuzsv3l9o" />
                        Volver
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </form>}
        </div>
      </div>
    </section>;
}